/**
 * The keyboard and placement rules of the kit's one menu (components/ui/Menu.tsx): the « Plus
 * d'actions » / « Colonnes » menu buttons and the DataGrid context menu (refonte WP-100, CP §15.6
 * « actions contextuelles », §11). Pure module — the components only wire it — so the rules are
 * unit-tested with plain objects.
 *
 * Keys (design/ux/01_foundations.md §3.13, WAI-ARIA menu): ↑ / ↓ move and wrap, Début / Fin, a
 * letter jumps to the next item starting with it, Entrée / Espace activate, Échap closes and gives
 * the focus back. A menu button closes on Tab (the focus goes on); the context menu keeps the focus
 * (Tab / Maj+Tab move like ↓ / ↑) until an item, Échap, a click outside or a scroll closes it.
 *
 * Disabled items: a menu button skips them (unchanged since WP-16); the context menu lets the
 * focus land on them so the reason is read with the item (`hint`), and Entrée does nothing.
 */
import { foldText } from "./combobox.js";
import { CONTEXT_MENU_ROW_INSET, CONTEXT_MENU_VIEWPORT_MARGIN } from "../config/ui.config.js";

/** True when the focus may rest on the item. */
function reachable(item, includeDisabled) {
  return Boolean(item) && (includeDisabled || !item.disabled);
}

/** Index of the first (`edge` "first") or last reachable item, -1 when none. */
export function menuEdge(items, edge, { includeDisabled = false } = {}) {
  const list = items ?? [];
  if (edge === "last") {
    for (let i = list.length - 1; i >= 0; i -= 1) if (reachable(list[i], includeDisabled)) return i;
    return -1;
  }
  return list.findIndex((it) => reachable(it, includeDisabled));
}

/** Next reachable index from `from` in direction `dir` (±1), wrapping; `from` when none. */
export function menuStep(items, from, dir, { includeDisabled = false } = {}) {
  const list = items ?? [];
  const n = list.length;
  let i = from;
  for (let k = 0; k < n; k += 1) {
    i = (i + dir + n) % n;
    if (reachable(list[i], includeDisabled)) return i;
  }
  return from;
}

/** Next reachable item after `from` whose label starts with `ch` (accents and case folded). */
export function menuTypeahead(items, from, ch, { includeDisabled = false } = {}) {
  const list = items ?? [];
  const n = list.length;
  const c = foldText(ch);
  if (!c) return from;
  for (let k = 1; k <= n; k += 1) {
    const i = (from + k + n) % n;
    if (reachable(list[i], includeDisabled) && foldText(list[i].label).startsWith(c)) return i;
  }
  return from;
}

/**
 * What a key does inside an open menu.
 * @param {{key: string, shiftKey?: boolean, ctrlKey?: boolean, metaKey?: boolean, altKey?: boolean}} e
 * @param {{ trapTab?: boolean }} [opts] `trapTab`: Tab / Maj+Tab move instead of leaving (context menu)
 * @returns {null | "next" | "prev" | "first" | "last" | "close" | "leave" | "activate" | "type"}
 */
export function menuKeyAction(e, { trapTab = false } = {}) {
  switch (e.key) {
    case "ArrowDown":
      return "next";
    case "ArrowUp":
      return "prev";
    case "Home":
      return "first";
    case "End":
      return "last";
    case "Escape":
      return "close";
    case "Tab":
      if (!trapTab) return "leave";
      return e.shiftKey ? "prev" : "next";
    case "Enter":
    case " ":
    case "Spacebar":
      return "activate";
    default:
      if (typeof e.key === "string" && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) return "type";
      return null;
  }
}

/**
 * Where a context menu sits (fixed position, CSS pixels). `anchor` is the pointer of a right-click
 * (`{ x, y }`) or, from the keyboard, the active row (`{ x, y, top, fromKeyboard: true }`: `y` its
 * bottom edge, `top` its top edge). The menu opens down and to the right of the anchor; without
 * room below it goes above (above the row when opened from the keyboard), without room on the
 * right it goes to the left of the pointer — always inside the viewport less the margin, and a menu
 * taller than the viewport scrolls in itself (`maxHeight`).
 * @param {{x: number, y: number, top?: number, fromKeyboard?: boolean}} anchor
 * @param {{width: number, height: number}} size  the rendered menu
 * @param {{width: number, height: number}} viewport
 * @param {number} [margin]
 * @returns {{top: number, left: number, maxHeight: number}}
 */
export function contextMenuPosition(anchor, size, viewport, margin = CONTEXT_MENU_VIEWPORT_MARGIN) {
  const maxHeight = Math.max(0, viewport.height - 2 * margin);
  const h = Math.min(size.height, maxHeight);
  const w = size.width;
  let left = anchor.x;
  if (left + w > viewport.width - margin && !anchor.fromKeyboard) left = anchor.x - w;
  left = Math.max(margin, Math.min(left, viewport.width - margin - w));
  let top = anchor.y;
  if (top + h > viewport.height - margin) top = (anchor.top ?? anchor.y) - h;
  top = Math.max(margin, Math.min(top, viewport.height - margin - h));
  return { top: Math.round(top), left: Math.round(left), maxHeight };
}

/**
 * The keyboard anchor of a row: under its first data cell (the row header when there is one),
 * inset so the menu reads as attached to that row, never to the selection column.
 * @param {{left: number, top: number, bottom: number}} rowRect
 * @param {{left: number} | null} [cellRect]
 */
export function rowMenuAnchor(rowRect, cellRect = null) {
  return {
    x: (cellRect?.left ?? rowRect.left) + CONTEXT_MENU_ROW_INSET,
    y: rowRect.bottom,
    top: rowRect.top,
    fromKeyboard: true,
  };
}
